import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Loader } from '../components/ui/loader';
import { themeAPI } from '@/utils/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyTheme = useCallback((themeData) => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', themeData.primary_color);
    root.style.setProperty('--secondary-color', themeData.secondary_color);
    root.style.setProperty('--background-color', themeData.background_color);
    root.style.setProperty('--text-color', themeData.text_color);
    
    // Calculate button text color based on secondary color brightness
    const buttonTextColor = getContrastColor(themeData.secondary_color);
    root.style.setProperty('--button-text-color', buttonTextColor);
    
    // Set card colors with theme awareness
    root.style.setProperty('--card-bg', `${themeData.primary_color}B3`); // 70% opacity
    root.style.setProperty('--card-border', `${themeData.secondary_color}1A`); // 10% opacity
    
    // Apply to body immediately
    document.body.style.backgroundColor = themeData.primary_color;
  }, []);

  const fetchTheme = useCallback(async () => {
    try {
      const res = await themeAPI.getActive();
      applyTheme(res.data);
      setTheme(res.data);
    } catch (error) {
      console.error('Error fetching theme:', error);
      // Apply default theme
      const defaultTheme = {
        preset_name: "Corporate Premium",
        primary_color: "#0B1C2D",
        secondary_color: "#C6A24F",
        background_color: "#FFFFFF",
        text_color: "#F9FAFB"
      };
      applyTheme(defaultTheme);
      setTheme(defaultTheme);
    } finally {
      setLoading(false);
    }
  }, [applyTheme]);

  // Calculate contrast color for text on colored backgrounds
  const getContrastColor = (hexColor) => {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black or white based on luminance
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  const refreshTheme = async () => {
    await fetchTheme();
  };

  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B1C2D' }}>
        <Loader className="text-secondary" />
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{ theme, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
